/**
 * The traffic fleet: cars, cyclists and pedestrians. Owned by `traffic`.
 *
 * Everything lives in lane space — (edge, s, lat) — so lane keeping, car following and
 * overtaking are one-dimensional problems and the whole fleet steps in well under the 1.2 ms
 * budget. World position is derived at the end of the step, never integrated.
 *
 * ALLOCATION (N9). Every agent, every scratch value and every spatial bucket is allocated in
 * the constructor and reused forever. `step()` allocates nothing. The obstacle index is a
 * per-edge singly-linked list over three typed arrays, rebuilt from scratch each step in
 * O(agents) with no hashing and no garbage.
 *
 * DETERMINISM (N4/§6). No Math.random, no wall clock. Every draw comes from the fork handed
 * in by the subsystem, and every animation phase is a pure function of `ctx.time.sim` plus a
 * per-agent constant, so `freezeClocks()` is satisfied for free.
 *
 * WHAT IT IS FOR. Traffic exists to make speed cost something. Cars run at roughly half the
 * player's top speed so you are always closing on one; cyclists sit 1.85 m outboard of the
 * car lane so passing one is a decision; pedestrians scatter so that hitting them reads as a
 * mistake rather than a mechanic.
 */

import * as THREE from 'three';
import { TUNING } from './tuning.js';
import { clamp, clamp01, damp, dampAngle, angleDelta, TAU } from '../core/mathx.js';
import { paintColours, taxiColour, busColour, jacketColours, coatColours, trouserColours, skinColours } from './render.js';

const T = TUNING;
const P = T.population;
const C = T.car;
const CY = T.cyclist;
const PD = T.ped;
const IM = T.impact;

const PED_WALK = 0, PED_CROSS = 1, PED_FLEE = 2;

/** Truck impulse sink before `vehicle` binds one. Keeps `_hit` branch-free (§4.10). */
const NO_SHOVE = () => {};

/* ══════════════════════════════════════════════════════════════════ factories ══ */

function makeCar(id) {
  return {
    id, kindIdx: 0, kind: 'sedan', active: false,
    edge: 0, s: 0, lat: 0, lane: 0,
    speed: 0, desired: 10, cruise: 10,
    brakeDemand: 0, braking: false, brakeHold: 0,
    /** Front-wheel steering angle, radians, +ve = to the car's right. Presentation only. */
    steer: 0,

    nextEdge: -1, turnDir: 0, planned: false,
    stopServed: false, dwell: 0, yieldT: 0,

    corner: false, cT: 0, cLen: 1,
    c0x: 0, c0z: 0, c1x: 0, c1z: 0, c2x: 0, c2z: 0,

    latVel: 0, spin: 0, spinYaw: 0, shoveT: 0,
    hop: 0, hopV: 0, bob: 0, bobV: 0,
    hitCd: 0, nmMin: 99, nmArmed: false, nmCd: 0,

    wheelSpin: 0, blinkOff: 0, honkT: 0, honkCd: 0, colour: null,
    /** Previous PATH yaw (no collision spin), for the Ackermann steer derivation. */
    pathYaw: 0, wheelbase: 2.7,
    x: 0, y: 0, z: 0, yaw: 0, pitch: 0, roll: 0,
    ySample: 0, sampleTick: 0,
    radius: 1.6, halfL: 2.2, halfW: 0.95,
    position: new THREE.Vector3(),
    velocity: new THREE.Vector3(),
  };
}

function makeCyclist(id) {
  return {
    id, kind: 'cyclist', active: false,
    edge: 0, s: 0, lat: 0,
    speed: 0, cruise: 5.5, desired: 5.5,
    nextEdge: -1, planned: false,
    wobblePhase: 0, bobPhase: 0,
    latVel: 0, shoveT: 0, spin: 0, spinYaw: 0,
    hitCd: 0, nmMin: 99, nmArmed: false, nmCd: 0, spookCd: 0,
    colour: null,
    x: 0, y: 0, z: 0, yaw: 0, roll: 0,
    ySample: 0, sampleTick: 0,
    radius: 0.55, halfL: 0.95, halfW: 0.34,
    position: new THREE.Vector3(),
    velocity: new THREE.Vector3(),
  };
}

function makePed(id) {
  return {
    id, kind: 'ped', active: false,
    edge: 0, s: 0, side: 1, dir: 1,
    lat: 0, latTarget: 0,
    speed: 0, cruise: 1.3,
    state: PED_WALK, fleeT: 0, crossT: 0,
    phase: 0, coat: null, trouser: null, skin: null,
    /** How far across the pavement this one walks; signed by `dir`. See TUNING.ped.latBias. */
    latMag: 0,
    /**
     * Silhouette identity: which body geometry (and therefore which ARM pose), which STRIDE
     * geometry, and this figure's build. All set at spawn. `arch` and `legPose` are drawn
     * independently, which is what makes 3 x 3 poses out of 6 meshes.
     */
    arch: 0, legPose: 0, scaleXZ: 1, scaleY: 1,
    hitCd: 0, spookCd: 0, sprawl: 0,
    x: 0, y: 0, z: 0, yaw: 0, lean: 0,
    ySample: 0, sampleTick: 0,
    radius: PD.radius,
    position: new THREE.Vector3(),
    velocity: new THREE.Vector3(),
  };
}

/* ═══════════════════════════════════════════════════════════════════ the fleet ══ */

export class Fleet {
  /**
   * @param {object} ctx    game context
   * @param {LaneGraph} graph
   * @param {Rng} rng       the traffic fork
   * @param {TrafficRig} rig
   * @param {string[]} kindList vehicle kind names, index-aligned with the rig
   */
  constructor(ctx, graph, rng, rig, kindList) {
    this.ctx = ctx;
    this.graph = graph;
    this.rng = rng;
    this.rig = rig;
    this.kindList = kindList;

    const scale = clamp(ctx.config.quality.trafficScale, 0.2, 1.5);
    this.targetCars = Math.min(P.maxCars, Math.round(P.cars * scale));
    this.targetCyclists = Math.min(P.maxCyclists, Math.round(P.cyclists * scale));
    this.targetPeds = Math.min(P.maxPeds, Math.round(P.peds * scale));

    this.cars = new Array(P.maxCars);
    for (let i = 0; i < P.maxCars; i++) this.cars[i] = makeCar(i);
    this.cyclists = new Array(P.maxCyclists);
    for (let i = 0; i < P.maxCyclists; i++) this.cyclists[i] = makeCyclist(i);
    this.peds = new Array(P.maxPeds);
    for (let i = 0; i < P.maxPeds; i++) this.peds[i] = makePed(i);

    this.liveCars = 0; this.liveCyclists = 0; this.livePeds = 0;

    // ── colour tables (linear rgb triples, built once)
    this.paints = paintColours();
    this.taxiCol = taxiColour();
    this.busCol = busColour();
    this.jackets = jacketColours();
    this.coats = coatColours();
    this.trousers = trouserColours();
    this.skins = skinColours();

    /**
     * ── COAT AND TROUSER ARE DEALT FROM A SHUFFLED BAG, NOT PICKED AT RANDOM ── round 12.
     *
     * A blind critic: *"the three red figures are the SAME MESH IN THE SAME POSE within 20 px
     * of each other."* The pose half of that is geometry (see TUNING.pedArmPoses); the RED half
     * is this. With 7 coats and an independent uniform draw per spawn, the chance that any
     * given pair of neighbours matches is 1/7, and across a 48-strong crowd a same-colour
     * triple is not unlikely — it is expected. Measured on the round-11 `winter-slush` capture:
     * two orange coats 62 px apart and two more 100 px apart, in one frame.
     *
     * Dealing from a shuffled permutation instead makes a repeat impossible until the whole
     * palette has been spent, so the nearest same-coat figure is on average 7 spawns away.
     * Deterministic — the shuffle consumes the traffic fork like everything else — and it costs
     * one array index per spawn instead of one rng call.
     */
    this._coatBag = new Int32Array(this.coats.length);
    this._coatBagI = this.coats.length;          // forces a shuffle on the first draw
    this._trouserBag = new Int32Array(this.trousers.length);
    this._trouserBagI = this.trousers.length;

    // ── vehicle kind mix, as parallel arrays for a cheap weighted pick
    this.kindWeights = kindList.map((k) => T.visual.kindWeights[k] || 1);
    this.paintWeights = T.visual.paintWeights;

    // ── obstacle index: per-edge singly linked list over typed arrays
    const N = P.maxCars + P.maxCyclists + P.maxPeds;
    this.obEdge = new Int32Array(N);
    this.obS = new Float32Array(N);
    this.obLat = new Float32Array(N);
    this.obSpeed = new Float32Array(N);
    this.obHalf = new Float32Array(N);
    this.obNext = new Int32Array(N);
    this.obTag = new Int32Array(N);            // unique agent tag so nobody blocks themselves
    this.bucketHead = new Int32Array(graph.edgeCount).fill(-1);
    this.obCount = 0;

    // ── published lists (live references; consumers must not mutate)
    this.activeCars = [];
    this.activeCyclists = [];
    this.activePeds = [];
    this.allActive = [];

    /** Potholes in state 'fresh' that a traffic vehicle drove over this step. Gameplay polls. */
    this.patchHits = [];

    /** Bound by the subsystem in init(); a no-op until then so `_hit` never branches. */
    this._shove = NO_SHOVE;
    /** The static parked field (parked.js). Bound in init(); null only in unit tests. */
    this.parked = null;
    this._n2 = new Float32Array(2);
    this._hitPos = new THREE.Vector3();
    this._spookBudget = PD.spookEventsPerSec;
    this._scanTick = 0;
    this._lastPlayerX = 0;
    this._lastPlayerZ = 0;
    this._havePlayer = false;
    // `quarantined` must stay 0. See `_quarantineBroken()`.
    this.stats = { spawned: 0, despawned: 0, hits: 0, nearMisses: 0, spooks: 0, quarantined: 0 };
    this._lastQuarantine = null;
  }

  /* ────────────────────────────────────────────────────────────────── spawning ── */

  _pickKind() {
    let total = 0;
    for (let i = 0; i < this.kindWeights.length; i++) total += this.kindWeights[i];
    let r = this.rng.next() * total;
    for (let i = 0; i < this.kindWeights.length; i++) {
      r -= this.kindWeights[i];
      if (r <= 0) return i;
    }
    return 0;
  }

  _pickPaint() {
    let total = 0;
    for (let i = 0; i < this.paintWeights.length; i++) total += this.paintWeights[i];
    let r = this.rng.next() * total;
    for (let i = 0; i < this.paintWeights.length; i++) {
      r -= this.paintWeights[i];
      if (r <= 0) return i;
    }
    return 0;
  }

  /**
   * Is (x,z) inside the streaming ring around the player?
   *
   * `forward` biases most spawns into the cone the player is driving into. Without it the
   * ring spreads 40 cars over 40 000 m² of a 400 m grid and the street you are actually
   * looking down comes up empty, which is exactly what the first street-speed capture showed.
   */
  _inRing(x, z, px, pz, lo, hi, forward) {
    const dx = x - px, dz = z - pz;
    const d2 = dx * dx + dz * dz;
    if (d2 < lo * lo || d2 > hi * hi) return false;
    if (forward) {
      const f = this.ctx.player.forward;
      const d = Math.sqrt(d2) || 1;
      if ((dx / d) * f.x + (dz / d) * f.z < P.forwardDot) return false;
    }
    return true;
  }

  /** True when nothing already sits within `clearance` of (edge, s). */
  _laneClear(edgeIdx, s, clearance, lat) {
    for (let i = 0; i < this.cars.length; i++) {
      const c = this.cars[i];
      if (!c.active || c.edge !== edgeIdx) continue;
      if (Math.abs(c.s - s) < clearance && Math.abs(c.lat - lat) < 2.4) return false;
    }
    for (let i = 0; i < this.cyclists.length; i++) {
      const c = this.cyclists[i];
      if (!c.active || c.edge !== edgeIdx) continue;
      if (Math.abs(c.s - s) < clearance * 0.6 && Math.abs(c.lat - lat) < 1.8) return false;
    }
    return true;
  }

  _freeCar() { for (let i = 0; i < this.cars.length; i++) if (!this.cars[i].active) return this.cars[i]; return null; }
  _freeCyclist() { for (let i = 0; i < this.cyclists.length; i++) if (!this.cyclists[i].active) return this.cyclists[i]; return null; }
  _freePed() { for (let i = 0; i < this.peds.length; i++) if (!this.peds[i].active) return this.peds[i]; return null; }

  spawnCar(px, pz, tries) {
    const g = this.graph;
    if (g.edgeCount === 0) return false;
    const car = this._freeCar();
    if (!car) return false;

    for (let attempt = 0; attempt < tries; attempt++) {
      const fwd = attempt < tries * P.forwardBias;
      const e = g.edges[this.rng.int(0, g.edgeCount)];
      const s = this.rng.range(2, Math.max(3, e.len - 2));
      const lane = e.lanes > 1 ? this.rng.int(0, e.lanes) : 0;
      const lat = g.laneOffset(e, lane);
      const x = g.worldX(e, s, lat), z = g.worldZ(e, s, lat);
      if (!this._inRing(x, z, px, pz, P.spawnMin, P.spawnMax, fwd)) continue;
      if (!this._laneClear(e.id, s, P.laneClearance, lat)) continue;

      const kindIdx = this._pickKind();
      const kind = this.kindList[kindIdx];
      const model = T.models[kind];

      car.active = true;
      car.kindIdx = kindIdx;
      car.kind = kind;
      car.edge = e.id; car.s = s; car.lat = lat; car.lane = lane;
      car.halfL = model.L * 0.5; car.halfW = model.W * 0.5;
      car.radius = model.W * 0.5 + 0.12;
      car.wheelbase = Math.max(1.6, (model.axleF - model.axleR) * model.L);

      const kScale = C.kindSpeedScale[kind] || 1;
      car.cruise = this.rng.range(C.speedMin, C.speedMax) * kScale * (e.kind === 'artere' ? C.artereBoost : 1);
      car.desired = car.cruise;
      car.speed = car.cruise * this.rng.range(0.7, 1.0);

      car.nextEdge = -1; car.planned = false; car.turnDir = 0;
      car.stopServed = true;            // do not stop at the very first node you meet
      car.dwell = 0; car.yieldT = 0;
      car.corner = false; car.cT = 0;
      car.latVel = 0; car.spin = 0; car.spinYaw = 0; car.shoveT = 0;
      car.hop = 0; car.hopV = 0; car.bob = 0; car.bobV = 0;
      car.hitCd = 0; car.nmMin = 99; car.nmArmed = false; car.nmCd = 0;
      car.wheelSpin = this.rng.range(0, TAU);
      car.blinkOff = this.rng.next();
      car.honkT = 0; car.honkCd = 0;
      car.braking = false; car.brakeDemand = 0; car.brakeHold = 0;
      car.steer = 0;
      car.sampleTick = this.rng.int(0, 3);

      if (kind === 'taxi') car.colour = this.taxiCol;
      else if (kind === 'bus') car.colour = this.busCol;
      else car.colour = this.paints[this._pickPaint()];

      const gr = this.ctx.world.sampleGround(x, z);
      car.ySample = gr.y; car.y = gr.y;
      car.x = x; car.z = z;
      car.yaw = Math.atan2(e.dx, e.dz);
      car.pathYaw = car.yaw;              // or step 1 sees a yaw jump and flicks the wheels
      this.stats.spawned++;
      return true;
    }
    return false;
  }

  spawnCyclist(px, pz, tries) {
    const g = this.graph;
    if (g.edgeCount === 0) return false;
    const cy = this._freeCyclist();
    if (!cy) return false;

    for (let attempt = 0; attempt < tries; attempt++) {
      const fwd = attempt < tries * P.forwardBias;
      const e = g.edges[this.rng.int(0, g.edgeCount)];
      const s = this.rng.range(2, Math.max(3, e.len - 2));
      const lat = g.cyclistOffset(e);
      const x = g.worldX(e, s, lat), z = g.worldZ(e, s, lat);
      if (!this._inRing(x, z, px, pz, P.spawnMin * 0.7, P.spawnMax * 0.8, fwd)) continue;
      if (!this._laneClear(e.id, s, 9, lat)) continue;

      cy.active = true;
      cy.edge = e.id; cy.s = s; cy.lat = lat;
      cy.cruise = this.rng.range(CY.speedMin, CY.speedMax);
      cy.desired = cy.cruise;
      cy.speed = cy.cruise * this.rng.range(0.8, 1.0);
      cy.nextEdge = -1; cy.planned = false;
      cy.wobblePhase = this.rng.range(0, TAU);
      cy.bobPhase = this.rng.range(0, TAU);
      cy.latVel = 0; cy.shoveT = 0; cy.spin = 0; cy.spinYaw = 0;
      cy.hitCd = 0; cy.nmMin = 99; cy.nmArmed = false; cy.nmCd = 0; cy.spookCd = 0;
      cy.colour = this.jackets[this.rng.int(0, this.jackets.length)];
      cy.sampleTick = this.rng.int(0, 3);
      const gr = this.ctx.world.sampleGround(x, z);
      cy.ySample = gr.y; cy.y = gr.y;
      cy.x = x; cy.z = z;
      cy.yaw = Math.atan2(e.dx, e.dz);
      this.stats.spawned++;
      return true;
    }
    return false;
  }

  /**
   * The lateral offset THIS pedestrian walks at on `side` of `e`.
   *
   * `LaneGraph.walkOffset` returns one centre-line per side, so every walker on a pavement
   * shared the exact same line and they stacked. The offset is `side · dir · latMag`:
   * multiplying by `side` makes a positive value mean "further from the road" on both
   * pavements, and multiplying by `dir` is the KEEP-RIGHT rule that stops opposing walkers
   * merging into one figure. See TUNING.ped.latBias.
   */
  _pedLat(p, e, side) {
    return this.graph.walkOffset(e, side) + side * p.dir * p.latMag;
  }

  /**
   * Deal the next index from a shuffled bag, reshuffling (Fisher-Yates, seeded) when spent.
   * See the note where `_coatBag` is allocated for why this is not `rng.int(0, n)`.
   *
   * Returns the index and advances the cursor. Allocation-free: the bag is a preallocated
   * Int32Array and this only ever swaps inside it.
   */
  _deal(bag, cursorKey) {
    let i = this[cursorKey];
    if (i >= bag.length) {
      for (let k = 0; k < bag.length; k++) bag[k] = k;
      for (let k = bag.length - 1; k > 0; k--) {
        const j = this.rng.int(0, k + 1);
        const t = bag[k]; bag[k] = bag[j]; bag[j] = t;
      }
      i = 0;
    }
    this[cursorKey] = i + 1;
    return bag[i];
  }

  spawnPed(px, pz, tries) {
    const g = this.graph;
    if (g.edgeCount === 0) return false;
    const p = this._freePed();
    if (!p) return false;

    // Drawn ONCE per spawn, not once per attempt: `_pedLat` reads both of these, and values
    // that moved between rejected attempts would make placement depend on how many attempts
    // it took. `dir` must be set BEFORE the first `_pedLat` call — it carries the keep-right
    // sign — which is why it is no longer assigned down with the rest of the state below.
    p.latMag = this.rng.range(PD.latBiasMin, PD.latBias);
    p.dir = this.rng.chance(0.5) ? 1 : -1;

    for (let attempt = 0; attempt < tries; attempt++) {
      const fwd = attempt < tries * P.forwardBias;
      const e = g.edges[this.rng.int(0, g.edgeCount)];
      const s = this.rng.range(1.5, Math.max(2.5, e.len - 1.5));
      const side = this.rng.chance(0.5) ? 1 : -1;
      const lat = this._pedLat(p, e, side);
      const x = g.worldX(e, s, lat), z = g.worldZ(e, s, lat);
      if (!this._inRing(x, z, px, pz, P.pedSpawnMin, P.pedSpawnMax, fwd)) continue;

      p.active = true;
      p.edge = e.id; p.s = s; p.side = side;
      p.lat = lat; p.latTarget = lat;
      p.cruise = this.rng.range(PD.walkMin, PD.walkMax);
      p.speed = p.cruise;
      p.state = PED_WALK; p.fleeT = 0; p.crossT = 0;
      p.phase = this.rng.range(0, TAU);
      p.coat = this.coats[this._deal(this._coatBag, '_coatBagI')];
      // Trousers are drawn INDEPENDENTLY of the coat — that independence is the whole point
      // of the third mesh. See TUNING.pedModel.trouserHexes.
      p.trouser = this.trousers[this._deal(this._trouserBag, '_trouserBagI')];
      p.skin = this.skins[this.rng.int(0, this.skins.length)];
      p.arch = T.pedArchetypeBag[this.rng.int(0, T.pedArchetypeBag.length)];
      // Stride geometry, drawn independently of the archetype: 3 arm poses x 3 leg poses.
      p.legPose = T.pedLegPoseBag[this.rng.int(0, T.pedLegPoseBag.length)];
      // A figure whose legs are modelled STANDING must not stride across the pavement at
      // walking pace, or the pose reads as a mannequin on a conveyor. Idle figures loiter.
      if (T.pedLegPoses[p.legPose].id === 'idle') { p.cruise *= PD.idleSpeedScale; p.speed = p.cruise; }
      const PM = T.pedModel;
      p.scaleY = this.rng.range(PM.scaleYMin, PM.scaleYMax);
      p.scaleXZ = this.rng.range(PM.scaleXZMin, PM.scaleXZMax);
      p.hitCd = 0; p.spookCd = 0; p.sprawl = 0;
      p.sampleTick = this.rng.int(0, 3);
      const gr = this.ctx.world.sampleGround(x, z);
      p.ySample = gr.y; p.y = gr.y;
      p.x = x; p.z = z;
      p.yaw = Math.atan2(e.dx * p.dir, e.dz * p.dir);
      this.stats.spawned++;
      return true;
    }
    return false;
  }

  /** Empty the world. Used by reset() and by the teleport rebuild. */
  clear() {
    for (let i = 0; i < this.cars.length; i++) this.cars[i].active = false;
    for (let i = 0; i < this.cyclists.length; i++) this.cyclists[i].active = false;
    for (let i = 0; i < this.peds.length; i++) this.peds[i].active = false;
    this.liveCars = this.liveCyclists = this.livePeds = 0;
  }

  /**
   * Fill the streaming ring in one go. Called at init, at reset, and whenever the player is
   * teleported by the harness — a shot must never open on an empty street.
   *
   * A single failed attempt is NOT a reason to stop. Spawn placement is rejection sampling
   * over ~170 directed edges of which only a fifth are in the ring at any moment, so a miss
   * is routine. Round 1 broke out of the loop on the first miss and the world booted with 13
   * agents instead of 98.
   */
  bulkFill(px, pz) {
    this._fillGroup(this.cars, this.targetCars, (t) => this.spawnCar(px, pz, t));
    this._fillGroup(this.cyclists, this.targetCyclists, (t) => this.spawnCyclist(px, pz, t));
    this._fillGroup(this.peds, this.targetPeds, (t) => this.spawnPed(px, pz, t));
    this._recount();
  }

  _fillGroup(arr, target, spawn) {
    let fails = 0, guard = 0;
    while (this._count(arr) < target && guard++ < 600) {
      if (spawn(P.bulkTries)) fails = 0;
      else if (++fails >= P.bulkGiveUp) break;
    }
  }

  _count(arr) { let n = 0; for (let i = 0; i < arr.length; i++) if (arr[i].active) n++; return n; }

  _recount() {
    this.liveCars = this._count(this.cars);
    this.liveCyclists = this._count(this.cyclists);
    this.livePeds = this._count(this.peds);
  }

  /* ──────────────────────────────────────────────────────────── obstacle index ── */

  _buildIndex() {
    const head = this.bucketHead;
    head.fill(-1);
    let n = 0;

    for (let i = 0; i < this.cars.length; i++) {
      const c = this.cars[i];
      if (!c.active) continue;
      this.obEdge[n] = c.edge; this.obS[n] = c.s; this.obLat[n] = c.lat;
      this.obSpeed[n] = c.speed; this.obHalf[n] = c.halfL; this.obTag[n] = i;
      this.obNext[n] = head[c.edge]; head[c.edge] = n; n++;
    }
    for (let i = 0; i < this.cyclists.length; i++) {
      const c = this.cyclists[i];
      if (!c.active) continue;
      this.obEdge[n] = c.edge; this.obS[n] = c.s; this.obLat[n] = c.lat;
      this.obSpeed[n] = c.speed; this.obHalf[n] = c.halfL; this.obTag[n] = 1000 + i;
      this.obNext[n] = head[c.edge]; head[c.edge] = n; n++;
    }
    // Only pedestrians actually IN the carriageway are obstacles. A ped on the sidewalk that
    // stopped traffic would be both wrong and infuriating.
    for (let i = 0; i < this.peds.length; i++) {
      const p = this.peds[i];
      if (!p.active) continue;
      const e = this.graph.edges[p.edge];
      if (Math.abs(p.lat) > e.halfW) continue;
      this.obEdge[n] = p.edge; this.obS[n] = p.s; this.obLat[n] = p.lat;
      this.obSpeed[n] = 0; this.obHalf[n] = 0.5; this.obTag[n] = 2000 + i;
      this.obNext[n] = head[p.edge]; head[p.edge] = n; n++;
    }
    this.obCount = n;
  }

  /**
   * Closest thing ahead on `edgeIdx` within `look` metres of `s`. Writes into _gap/_gapSpeed
   * because returning an object per call per agent would allocate 100× per step.
   */
  _scanEdge(edgeIdx, s, lat, look, myTag, myHalf, offset) {
    const head = this.bucketHead;
    for (let i = head[edgeIdx]; i >= 0; i = this.obNext[i]) {
      if (this.obTag[i] === myTag) continue;
      const d = this.obS[i] - s + offset;
      if (d <= 0 || d > look) continue;
      if (Math.abs(this.obLat[i] - lat) > C.laneBlockHalfWidth + this.obHalf[i] * 0.25) continue;
      const gap = d - this.obHalf[i] - myHalf;
      if (gap < this._gap) { this._gap = gap; this._gapSpeed = this.obSpeed[i]; }
    }
  }

  /* ──────────────────────────────────────────────────────── NaN quarantine ── */

  /**
   * ⚠ A NON-FINITE AGENT MUST NEVER LEAVE THIS SUBSYSTEM. Round 12, and it was a
   * game-breaker: the player drives, an agent's x/z goes NaN, and the whole 3D world vanishes
   * while the HUD keeps counting.
   *
   * The escape route is `_contactGroup → _hit → _shove → ctx.player.applyImpulse(NaN,…)`.
   * `ctx.player` is a PUBLISHED interface (§4.6) and `traffic` is not entitled to poison it,
   * whatever is wrong upstream. This pass runs after every agent has stepped and before
   * anything reads a position, so a broken agent is recycled in the same step it appears.
   *
   * WHY RECYCLE RATHER THAN CLAMP. A NaN agent could never despawn either — `_despawn` tests
   * `dx*dx + dz*dz > pedFar`, which is FALSE for NaN, so the agent stayed active forever, held
   * a slot against `targetPeds` so no replacement spawned, and sat in the obstacle grid. It was
   * permanently dead weight as well as a poison source. Freeing the slot fixes both.
   *
   * `stats.quarantined` is the oracle: it must stay 0. It is deliberately a counter and not a
   * silent repair, because a quarantine that nobody can see is how this survived 42 captures.
   */
  _quarantineBroken() {
    const groups = this._quarGroups || (this._quarGroups = [this.cars, this.cyclists, this.peds]);
    let hit = 0;
    for (let gi = 0; gi < groups.length; gi++) {
      const list = groups[gi];
      for (let i = 0; i < list.length; i++) {
        const a = list[i];
        if (!a.active) continue;
        // Note the sense: `>= 0` is FALSE for NaN, so this catches NaN and ±Infinity in one
        // test without a Number.isFinite call per field per agent per step.
        const mag = a.x * a.x + a.z * a.z + a.y * a.y;
        if (mag >= 0 && mag < 1e18) continue;
        a.active = false;
        hit++;
        this.stats.quarantined++;
        this._lastQuarantine = { kind: a.kind, id: a.id, x: a.x, y: a.y, z: a.z,
          s: a.s, lat: a.lat, speed: a.speed, dir: a.dir, edge: a.edge, state: a.state };
      }
    }
    // Only pay for the recount when something was actually recycled — this runs every step.
    if (hit) this._recount();
  }

  /* ─────────────────────────────────────────────────────────────────── stepping ── */

  step(dt) {
    const ctx = this.ctx;
    const player = ctx.player;
    const px = player.position.x, pz = player.position.z;
    const pSpeed = player.speed;

    // Teleport detection: the harness drops the truck into a scenario between steps, and an
    // empty street in a shot is a failed shot.
    if (this._havePlayer) {
      const jx = px - this._lastPlayerX, jz = pz - this._lastPlayerZ;
      if (jx * jx + jz * jz > P.teleportDist * P.teleportDist) {
        this.clear();
        this.bulkFill(px, pz);
      }
    } else {
      this._havePlayer = true;
    }
    this._lastPlayerX = px; this._lastPlayerZ = pz;

    this._spookBudget = Math.min(PD.spookEventsPerSec, this._spookBudget + PD.spookEventsPerSec * dt);
    this.patchHits.length = 0;

    this._despawn(px, pz);
    this._stream(px, pz);
    this._buildIndex();
    this._claimNodes();

    for (let i = 0; i < this.cars.length; i++) {
      const c = this.cars[i];
      if (c.active) this._stepCar(c, dt, px, pz, pSpeed);
    }
    for (let i = 0; i < this.cyclists.length; i++) {
      const c = this.cyclists[i];
      if (c.active) this._stepCyclist(c, dt, px, pz, pSpeed);
    }
    for (let i = 0; i < this.peds.length; i++) {
      const p = this.peds[i];
      if (p.active) this._stepPed(p, dt, px, pz, pSpeed);
    }

    // ⚠ QUARANTINE BEFORE ANYTHING READS A POSITION. See `_quarantineBroken`.
    this._quarantineBroken();

    this._potholeInteraction();
    this._playerContacts(dt, px, pz, pSpeed);
    this._parkedContacts(dt, px, pz, pSpeed);
    this._publish();
  }

  // ---------------------------------------------------------------- streaming

  _despawn(px, pz) {
    const far = P.despawnDist * P.despawnDist;
    const pedFar = P.pedDespawnDist * P.pedDespawnDist;
    for (let i = 0; i < this.cars.length; i++) {
      const c = this.cars[i];
      if (!c.active) continue;
      const dx = c.x - px, dz = c.z - pz;
      if (dx * dx + dz * dz > far) { c.active = false; this.stats.despawned++; }
    }
    for (let i = 0; i < this.cyclists.length; i++) {
      const c = this.cyclists[i];
      if (!c.active) continue;
      const dx = c.x - px, dz = c.z - pz;
      if (dx * dx + dz * dz > far) { c.active = false; this.stats.despawned++; }
    }
    for (let i = 0; i < this.peds.length; i++) {
      const p = this.peds[i];
      if (!p.active) continue;
      const dx = p.x - px, dz = p.z - pz;
      if (dx * dx + dz * dz > pedFar) { p.active = false; this.stats.despawned++; }
    }
    this._recount();
  }

  _stream(px, pz) {
    this._scanTick++;
    if (this._scanTick % P.scanInterval !== 0) return;
    let budget = P.spawnPerStep;
    while (budget > 0 && this.liveCars < this.targetCars) {
      if (!this.spawnCar(px, pz, 10)) break;
      this.liveCars++; budget--;
    }
    while (budget > 0 && this.liveCyclists < this.targetCyclists) {
      if (!this.spawnCyclist(px, pz, 10)) break;
      this.liveCyclists++; budget--;
    }
    while (budget > 0 && this.livePeds < this.targetPeds) {
      if (!this.spawnPed(px, pz, 10)) break;
      this.livePeds++; budget--;
    }
  }

  // ---------------------------------------------------------------- intersections

  /**
   * Two claims per node, one per road axis. Cars only ever yield to CROSSING traffic, so two
   * cars approaching the same node head-on down the same street never deadlock each other.
   * The closest claimant wins, which is fair and stays deterministic.
   */
  _claimNodes() {
    const g = this.graph;
    const nodes = g.nodes;
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i];
      n.claimA = -1; n.claimDistA = 1e9;
      n.claimB = -1; n.claimDistB = 1e9;
    }
    for (let i = 0; i < this.cars.length; i++) {
      const c = this.cars[i];
      if (!c.active) continue;
      const e = g.edges[c.edge];
      const d = c.corner ? 0 : e.len - c.s;
      if (d > C.nodeBusyRadius) continue;
      const n = nodes[e.to];
      if (Math.abs(e.dx) > 0.5) {
        if (d < n.claimDistA) { n.claimDistA = d; n.claimA = c.id; }
      } else if (d < n.claimDistB) { n.claimDistB = d; n.claimB = c.id; }
    }
  }

  /** True if `car` must give way to crossing traffic at the node ahead. */
  _mustYield(car, edge) {
    const n = this.graph.nodes[edge.to];
    const d = edge.len - car.s;
    if (Math.abs(edge.dx) > 0.5) {
      return n.claimB >= 0 && n.claimDistB < d;
    }
    return n.claimA >= 0 && n.claimDistA < d;
  }

  // ---------------------------------------------------------------- car

  _plan(car, edge) {
    if (car.planned) return;
    const next = this.graph.chooseNext(edge, this.rng);
    car.nextEdge = next;
    const n = this.graph.edges[next];
    const turn = n.dx * edge.rx + n.dz * edge.rz;      // >0 right, <0 left
    const straight = n.dx * edge.dx + n.dz * edge.dz;
    car.turnDir = straight > 0.85 ? 0 : (turn > 0 ? 1 : -1);
    car.planned = true;
  }

  _stepCar(car, dt, px, pz, pSpeed) {
    const g = this.graph;
    let e = g.edges[car.edge];
    const sim = this.ctx.time.sim;

    // ── decay any shove from a collision
    if (car.shoveT > 0) {
      car.shoveT -= dt;
      car.lat += car.latVel * dt;
      car.latVel -= car.latVel * Math.min(1, IM.recoverRate * dt);
      car.spinYaw += car.spin * dt;
      car.spin -= car.spin * Math.min(1, IM.recoverRate * 1.35 * dt);
      car.lat = clamp(car.lat, -IM.shoveLatMax, IM.shoveLatMax);
    } else {
      car.spinYaw = dampAngle(car.spinYaw, 0, 3.4, dt);
      car.latVel = 0;
      car.spin = 0;
    }
    if (car.hitCd > 0) car.hitCd -= dt;
    if (car.nmCd > 0) car.nmCd -= dt;

    // ── vertical: hop from an impact, bob from an open pothole
    car.hopV -= 26 * dt;
    car.hop += car.hopV * dt;
    if (car.hop < 0) { car.hop = 0; car.hopV = 0; }
    car.bobV += (-car.bob * 190 - car.bobV * 17) * dt;
    car.bob += car.bobV * dt;

    // ── plan the next edge early enough to indicate and slow down
    const remaining = e.len - car.s;
    if (!car.corner && remaining < C.cornerSlowDist) this._plan(car, e);

    // ── target speed
    let target = car.desired;

    if (car.corner) {
      if (C.cornerSpeed < target) target = C.cornerSpeed;
    } else if (car.planned && car.turnDir !== 0) {
      const t = C.cornerSpeed + Math.max(0, remaining - C.cornerRadius) * 0.35;
      if (t < target) target = t;
    }

    // Car following (this edge, then across the node onto the planned one).
    this._gap = Infinity; this._gapSpeed = 0;
    const tag = car.id;
    this._scanEdge(car.edge, car.s, car.lat, C.lookahead, tag, car.halfL, 0);
    if (remaining < C.lookahead && car.nextEdge >= 0) {
      this._scanEdge(car.nextEdge, 0, g.laneOffset(g.edges[car.nextEdge], car.lane),
        C.lookahead, tag, car.halfL, remaining);
    }
    if (this._gap < Infinity) {
      const follow = this._gapSpeed + (this._gap - C.headwayMin) * 0.85;
      if (follow < target) target = Math.max(0, follow);
    }

    // The player truck as an obstacle in my lane.
    const relX = px - car.x, relZ = pz - car.z;
    const along = relX * e.dx + relZ * e.dz;
    const across = relX * e.rx + relZ * e.rz;
    let playerBlocks = false;
    if (along > 0 && along < C.lookahead && Math.abs(across) < C.laneBlockHalfWidth + 0.9) {
      const gap = along - car.halfL - 2.6;
      const follow = Math.max(0, pSpeed * 0.5) + (gap - C.headwayMin) * 0.9;
      if (follow < target) target = Math.max(0, follow);
      playerBlocks = along < C.honkBlockDist;
    }

    // Held up by the player: lean on the horn. Cheapest characterisation in the game.
    if (car.honkCd > 0) car.honkCd -= dt;
    if (playerBlocks && car.speed < 2.5) {
      car.honkT += dt;
      if (car.honkT > C.honkAfter && car.honkCd <= 0) {
        car.honkT = 0;
        car.honkCd = C.honkCooldown;
        this.ctx.audio.sfx('horn', { pos: car.position, gain: 0.55 });
      }
    } else {
      car.honkT = 0;
    }

    // ── arrêt + give way
    let stopping = false;
    if (!car.corner && car.nextEdge >= 0) {
      const node = g.nodes[e.to];
      const needStop = node.arret && !car.stopServed;
      let yielding = this._mustYield(car, e) && remaining < C.nodeBusyRadius;
      if (yielding) {
        car.yieldT += dt;
        if (car.yieldT > C.yieldTimeout) yielding = false;    // deadlock breaker
      } else {
        car.yieldT = 0;
      }

      if (needStop || yielding) {
        const distToLine = remaining - C.stopLine;
        if (distToLine < 14) {
          stopping = true;
          const v = distToLine <= 0.05 ? 0 : Math.sqrt(Math.max(0, 2 * C.brake * distToLine));
          if (v < target) target = v;
        }
      }
      if (needStop && remaining < C.stopLine + 1.2 && car.speed < 0.8) {
        car.dwell += dt;
        if (car.dwell >= C.stopDwell) { car.stopServed = true; car.dwell = 0; }
      }
    }

    // ── longitudinal integration
    const dv = target - car.speed;
    if (dv > 0) car.speed += Math.min(dv, C.accel * dt);
    else {
      const rate = (stopping || target < 1) ? C.panicBrake : C.brake;
      car.speed += Math.max(dv, -rate * dt);
    }
    if (car.speed < 0) car.speed = 0;
    car.brakeDemand = dv < -0.5 ? clamp01(-dv / 4) : 0;
    // A brake lamp stays lit while the foot is down. Without the hold, a still frame catches
    // almost nobody braking and the whole fleet reads as parked — see `car.brakeLightHold`.
    if (car.brakeDemand > 0.12 || (stopping && car.speed < 3)) car.brakeHold = C.brakeLightHold;
    else if (car.brakeHold > 0) car.brakeHold -= dt;
    car.braking = car.brakeHold > 0;

    // ── lateral target: lane centre, plus a flinch away from the player
    let latTarget = g.laneOffset(e, car.lane);
    const pdx = px - car.x, pdz = pz - car.z;
    const pd2 = pdx * pdx + pdz * pdz;
    if (pd2 < C.shyRadius * C.shyRadius) {
      const side = (pdx * e.rx + pdz * e.rz) > 0 ? -1 : 1;
      const k = 1 - Math.sqrt(pd2) / C.shyRadius;
      latTarget += side * C.shyAmount * k;
    }
    // Clamp inside the DRIVABLE width, not the carriageway: past it is the parked line.
    latTarget = clamp(latTarget, 0.9, g.driveLimit(e, car.halfW));
    const latPrev = car.lat;
    if (car.shoveT <= 0) car.lat = damp(car.lat, latTarget, C.latRate, dt);
    const latRate = (car.lat - latPrev) / dt;

    // ── advance
    const move = car.speed * dt;

    if (car.corner) {
      car.cT += move / car.cLen;
      if (car.cT >= 1) {
        const n = g.edges[car.nextEdge];
        car.edge = n.id;
        car.s = C.cornerRadius;
        car.lane = Math.min(car.lane, n.lanes - 1);
        car.lat = g.laneOffset(n, car.lane);
        car.corner = false;
        car.planned = false;
        car.nextEdge = -1;
        car.stopServed = this.rng.next() > C.stopChance;
        car.yieldT = 0;
        e = n;
      }
    } else {
      car.s += move;
      const rem2 = e.len - car.s;
      const canGo = !(this._mustYield(car, e) && car.yieldT <= C.yieldTimeout)
        && (!g.nodes[e.to].arret || car.stopServed);

      if (car.planned && car.turnDir !== 0 && rem2 <= C.cornerRadius && canGo) {
        this._beginCorner(car, e);
      } else if (car.s >= e.len) {
        if (car.nextEdge < 0) this._plan(car, e);
        const n = g.edges[car.nextEdge];
        car.s -= e.len;
        car.edge = n.id;
        car.lane = Math.min(car.lane, n.lanes - 1);
        car.corner = false;
        car.planned = false;
        car.nextEdge = -1;
        car.stopServed = this.rng.next() > C.stopChance;
        car.yieldT = 0;
        if (e.dx * n.dx + e.dz * n.dz < 0.99) car.lat = g.laneOffset(n, car.lane);
        e = n;
      } else if (car.s > e.len - 0.02 && !canGo) {
        car.s = e.len - 0.02;
      }
    }

    // ── world pose
    let wx, wz, yaw;
    if (car.corner) {
      const t = clamp01(car.cT), u = 1 - t;
      wx = u * u * car.c0x + 2 * u * t * car.c1x + t * t * car.c2x;
      wz = u * u * car.c0z + 2 * u * t * car.c1z + t * t * car.c2z;
      const tx = 2 * (u * (car.c1x - car.c0x) + t * (car.c2x - car.c1x));
      const tz = 2 * (u * (car.c1z - car.c0z) + t * (car.c2z - car.c1z));
      yaw = Math.atan2(tx, tz);
    } else {
      wx = g.worldX(e, car.s, car.lat);
      wz = g.worldZ(e, car.s, car.lat);
      yaw = Math.atan2(e.dx, e.dz) - latRate * C.latYawGain;
    }

    car.sampleTick++;
    if ((car.sampleTick & 3) === 0) {
      const gr = this.ctx.world.sampleGround(wx, wz);
      car.ySample = gr.y;
    }
    const yBase = damp(car.y - car.hop - car.bob, car.ySample, 22, dt);

    /**
     * FRONT-WHEEL STEER, from the Ackermann relation `tan(δ) = yawRate · L / v`.
     *
     * Derived rather than authored so one expression covers a junction turn, a lane change and
     * a flinch away from the truck. Taken off the PATH yaw, before the collision spin is added,
     * because a car that has just been punted sideways is not steering into the spin.
     */
    const yawRate = angleDelta(car.pathYaw, yaw) / dt;
    car.pathYaw = yaw;
    const steerTarget = clamp(
      yawRate * car.wheelbase / Math.max(C.steerMinSpeed, car.speed) * C.steerGain,
      -C.steerMax, C.steerMax
    );
    car.steer = damp(car.steer, steerTarget, C.steerRate, dt);

    const prevX = car.x, prevZ = car.z;
    car.x = wx; car.z = wz;
    car.y = yBase + car.hop + car.bob;
    car.yaw = yaw + car.spinYaw;
    car.roll = clamp(-latRate * 0.035 + car.spin * 0.05, -0.22, 0.22);
    car.pitch = clamp(car.bobV * 0.010 - car.brakeDemand * 0.02, -0.09, 0.09);
    car.wheelSpin -= (car.speed * dt) / Math.max(0.15, T.models[car.kind].wheelR);

    car.velocity.set((car.x - prevX) / dt, 0, (car.z - prevZ) / dt);
    car.position.set(car.x, car.y, car.z);
  }

  _beginCorner(car, e) {
    const g = this.graph;
    const n = g.edges[car.nextEdge];
    const R = C.cornerRadius;
    const lane = Math.min(car.lane, n.lanes - 1);
    const outLat = g.laneOffset(n, lane);

    const p0x = g.worldX(e, e.len - R, car.lat);
    const p0z = g.worldZ(e, e.len - R, car.lat);
    const p2x = g.worldX(n, R, outLat);
    const p2z = g.worldZ(n, R, outLat);
    // Apex: project the exit point back onto the entry line. Exact tangency at both ends,
    // so the car never snaps its heading in the middle of an intersection.
    const proj = (p2x - p0x) * e.dx + (p2z - p0z) * e.dz;
    const p1x = p0x + e.dx * proj;
    const p1z = p0z + e.dz * proj;

    car.c0x = p0x; car.c0z = p0z;
    car.c1x = p1x; car.c1z = p1z;
    car.c2x = p2x; car.c2z = p2z;

    const l1 = Math.sqrt((p1x - p0x) * (p1x - p0x) + (p1z - p0z) * (p1z - p0z));
    const l2 = Math.sqrt((p2x - p1x) * (p2x - p1x) + (p2z - p1z) * (p2z - p1z));
    car.cLen = Math.max(1.5, (l1 + l2) * 0.88);
    car.cT = 0;
    car.corner = true;
  }

  // ---------------------------------------------------------------- cyclist

  _stepCyclist(cy, dt, px, pz, pSpeed) {
    const g = this.graph;
    let e = g.edges[cy.edge];
    const sim = this.ctx.time.sim;

    if (cy.shoveT > 0) {
      cy.shoveT -= dt;
      cy.lat += cy.latVel * dt;
      cy.latVel -= cy.latVel * Math.min(1, IM.recoverRate * dt);
      cy.spinYaw += cy.spin * dt;
      cy.spin -= cy.spin * Math.min(1, IM.recoverRate * 1.2 * dt);
    } else {
      cy.spinYaw = dampAngle(cy.spinYaw, 0, 2.6, dt);
      cy.latVel = 0; cy.spin = 0;
    }
    if (cy.hitCd > 0) cy.hitCd -= dt;
    if (cy.nmCd > 0) cy.nmCd -= dt;
    if (cy.spookCd > 0) cy.spookCd -= dt;

    let target = cy.desired;
    this._gap = Infinity; this._gapSpeed = 0;
    this._scanEdge(cy.edge, cy.s, cy.lat, CY.lookahead, 1000 + cy.id, cy.halfL, 0);
    if (this._gap < Infinity && this._gap < 8) {
      const follow = this._gapSpeed + (this._gap - 3.2) * 0.9;
      if (follow < target) target = Math.max(0, follow);
    }

    const dv = target - cy.speed;
    cy.speed += dv > 0 ? Math.min(dv, CY.accel * dt) : Math.max(dv, -CY.brake * dt);
    if (cy.speed < 0) cy.speed = 0;

    // Kerb lane + wobble + flinch from the truck.
    let latTarget = g.cyclistOffset(e);
    const pdx = px - cy.x, pdz = pz - cy.z;
    const pd2 = pdx * pdx + pdz * pdz;
    if (pd2 < CY.shyRadius * CY.shyRadius && pSpeed > 4) {
      const k = 1 - Math.sqrt(pd2) / CY.shyRadius;
      latTarget += CY.shyAmount * k;                  // always toward the kerb
      if (cy.spookCd <= 0 && k > 0.55) {
        cy.spookCd = 2.5;
        this.ctx.bus.emit('traffic:spooked', { kind: 'cyclist', position: { x: cy.x, y: cy.y + 1.2, z: cy.z } });
        this.stats.spooks++;
      }
    }
    latTarget = clamp(latTarget, 0.8, g.driveLimit(e, cy.halfW) + 0.45);

    const wobble = Math.sin(sim * CY.wobbleRate + cy.wobblePhase) * CY.wobbleAmp
      + Math.sin(sim * CY.wobbleRate * 0.41 + cy.wobblePhase * 1.7) * CY.wobbleAmp * 0.45;
    const latPrev = cy.lat;
    if (cy.shoveT <= 0) cy.lat = damp(cy.lat, latTarget + wobble, 3.6, dt);
    const latRate = (cy.lat - latPrev) / dt;

    cy.s += cy.speed * dt;
    if (cy.s >= e.len) {
      const next = g.chooseNext(e, this.rng);
      const n = g.edges[next];
      cy.s -= e.len;
      cy.edge = n.id;
      cy.lat = g.cyclistOffset(n);
      e = n;
    }

    const wx = g.worldX(e, cy.s, cy.lat);
    const wz = g.worldZ(e, cy.s, cy.lat);
    cy.sampleTick++;
    if ((cy.sampleTick & 3) === 0) cy.ySample = this.ctx.world.sampleGround(wx, wz).y;

    const bob = Math.sin(sim * CY.bobRate + cy.bobPhase) * CY.bobAmp * clamp01(cy.speed / 4);
    const prevX = cy.x, prevZ = cy.z;
    cy.x = wx; cy.z = wz;
    cy.y = damp(cy.y - (cy.y - cy.ySample) * 0, cy.ySample, 22, dt) + bob;
    cy.yaw = Math.atan2(e.dx, e.dz) - latRate * 0.10 + cy.spinYaw;
    cy.roll = clamp(-latRate * CY.rollGain * 0.30 - wobble * 0.55, -0.45, 0.45);

    cy.velocity.set((cy.x - prevX) / dt, 0, (cy.z - prevZ) / dt);
    cy.position.set(cy.x, cy.y, cy.z);
  }

  // ---------------------------------------------------------------- pedestrian

  _stepPed(p, dt, px, pz, pSpeed) {
    const g = this.graph;
    let e = g.edges[p.edge];
    const sim = this.ctx.time.sim;

    if (p.hitCd > 0) p.hitCd -= dt;
    if (p.spookCd > 0) p.spookCd -= dt;
    if (p.sprawl > 0) p.sprawl -= dt;

    // ── spook: close, fast and coming at me
    const pdx = px - p.x, pdz = pz - p.z;
    const pd2 = pdx * pdx + pdz * pdz;
    if (p.state !== PED_FLEE && pSpeed > PD.spookSpeed && pd2 < PD.spookRadius * PD.spookRadius) {
      const pv = this.ctx.player.velocity;
      const closing = -(pdx * pv.x + pdz * pv.z);        // player heading toward me
      if (closing > 0 && this._spookBudget >= 1 && p.spookCd <= 0) {
        this._spookBudget -= 1;
        p.spookCd = 3.0;
        p.state = PED_FLEE;
        p.fleeT = PD.fleeTime;
        // Run away from the road, and away from the truck along the pavement.
        p.dir = ((p.x - px) * e.dx + (p.z - pz) * e.dz) >= 0 ? 1 : -1;
        p.side = p.lat >= 0 ? 1 : -1;
        this.ctx.bus.emit('traffic:spooked', { kind: 'ped', position: { x: p.x, y: p.y + 1.0, z: p.z } });
        this.stats.spooks++;
      }
    }

    let speed = p.cruise;
    if (p.state === PED_FLEE) {
      speed = PD.fleeSpeed;
      p.fleeT -= dt;
      p.latTarget = this._pedLat(p, e, p.side) + p.side * 0.9;
      if (p.fleeT <= 0) { p.state = PED_WALK; p.latTarget = this._pedLat(p, e, p.side); }
    } else if (p.state === PED_CROSS) {
      speed = p.cruise * PD.crossSpeedScale;
      p.crossT -= dt;
      if (Math.abs(p.lat - p.latTarget) < 0.25 || p.crossT <= 0) {
        p.state = PED_WALK;
        p.side = p.latTarget >= 0 ? 1 : -1;
        p.latTarget = this._pedLat(p, e, p.side);
      }
    } else {
      p.latTarget = this._pedLat(p, e, p.side);
    }

    p.speed = damp(p.speed, speed, PD.accel, dt);
    p.lat = damp(p.lat, p.latTarget, p.state === PED_CROSS ? 1.9 : 3.0, dt);

    p.s += p.speed * p.dir * dt * (p.state === PED_CROSS ? 0.25 : 1);

    // ── node handling: continue, turn, or step off the kerb and cross
    if (p.s >= e.len || p.s <= 0) {
      const atEnd = p.s >= e.len;
      const node = atEnd ? e.to : e.from;
      if (this.rng.next() < PD.crossChance && p.state === PED_WALK) {
        p.state = PED_CROSS;
        p.crossT = 4.0;
        p.latTarget = this._pedLat(p, e, -p.side);
        p.s = clamp(p.s, 1.2, e.len - 1.2);
        p.dir = atEnd ? -1 : 1;
      } else {
        // Walk onto another street leaving this node.
        const outs = g.nodes[node].out;
        if (outs.length) {
          const nextIdx = outs[this.rng.int(0, outs.length)];
          const n = g.edges[nextIdx];
          p.edge = n.id;
          p.dir = 1;
          p.s = 0.6;
          p.side = this.rng.chance(0.5) ? 1 : -1;
          p.lat = this._pedLat(p, n, p.side);
          p.latTarget = p.lat;
          e = n;
        } else {
          p.dir = -p.dir;
          p.s = clamp(p.s, 0.5, e.len - 0.5);
        }
      }
    }
    p.s = clamp(p.s, 0, e.len);

    const wx = g.worldX(e, p.s, p.lat);
    const wz = g.worldZ(e, p.s, p.lat);
    p.sampleTick++;
    if ((p.sampleTick & 3) === 0) p.ySample = this.ctx.world.sampleGround(wx, wz).y;

    // An idle figure keeps a trace of the bob — a perfectly still one reads as a statue, which
    // is the same defect from the other end — but not a walker's.
    const idle = p.state === PED_WALK && T.pedLegPoses[p.legPose].id === 'idle';
    const cadence = p.state === PED_FLEE ? PD.bobRate * 1.9 : (idle ? PD.bobRate * 0.34 : PD.bobRate);
    const amp = p.state === PED_FLEE ? PD.bobAmp * 1.7 : (idle ? PD.bobAmp * 0.22 : PD.bobAmp);
    const walkPhase = sim * cadence + p.phase;
    const bob = Math.abs(Math.sin(walkPhase)) * amp;

    const prevX = p.x, prevZ = p.z;
    p.x = wx; p.z = wz;
    p.y = p.ySample + bob;

    let heading;
    if (p.state === PED_CROSS) {
      const s2 = p.latTarget > p.lat ? 1 : -1;
      heading = Math.atan2(e.rx * s2, e.rz * s2);
    } else {
      heading = Math.atan2(e.dx * p.dir, e.dz * p.dir);
    }
    p.yaw = heading + Math.sin(walkPhase) * PD.swayAmp;
    p.lean = p.state === PED_FLEE ? -0.20 : Math.sin(walkPhase * 0.5) * 0.02;
    if (p.sprawl > 0) p.lean = -1.25;

    p.velocity.set((p.x - prevX) / dt, 0, (p.z - prevZ) / dt);
    p.position.set(p.x, p.y, p.z);
  }

  // ---------------------------------------------------------------- potholes

  /**
   * Cars bounce through OPEN holes, and a car that drives across an uncured FRESH patch has
   * wrecked it. `gameplay` owns `patch:ruined`, so we detect and expose — we never emit it.
   */
  _potholeInteraction() {
    const holes = this.ctx.gameplay.potholes();
    // Indexed, not `for…of`. `gameplay.potholes()` hands back a REUSED array, but a for-of
    // over it still constructs an ArrayIterator every fixed step — 60 objects a second from
    // this one line, in a subsystem whose whole design premise is that `step()` allocates
    // nothing (N9). Guard for a non-array in case gameplay ever returns a true iterable.
    if (!holes || holes.length === undefined) return;
    for (let hi = 0; hi < holes.length; hi++) {
      const h = holes[hi];
      if (!h || (h.state !== 'open' && h.state !== 'fresh')) continue;
      const hx = h.position.x, hz = h.position.z;
      const r = (h.radius || 1.2) + C.potholeRadiusPad;
      const r2 = r * r;
      for (let i = 0; i < this.cars.length; i++) {
        const c = this.cars[i];
        if (!c.active) continue;
        const dx = c.x - hx, dz = c.z - hz;
        if (dx * dx + dz * dz > r2) continue;
        if (h.state === 'open') {
          if (c.bobV > -0.2 && c.bob > -0.02) c.bobV = -C.potholeBobAmp * (2 + c.speed * 0.16);
        } else {
          this.patchHits.push(h.id);
        }
      }
    }
  }

  // ---------------------------------------------------------------- player contacts

  _playerContacts(dt, px, pz, pSpeed) {
    void dt;
    const player = this.ctx.player;
    const truckR = (player.halfWidth != null ? player.halfWidth : 1.05) + IM.truckPad;
    const pvx = player.velocity.x, pvz = player.velocity.z;

    this._contactGroup(this.cars, px, pz, pSpeed, truckR, pvx, pvz, 0);
    this._contactGroup(this.cyclists, px, pz, pSpeed, truckR, pvx, pvz, 1);
    this._contactGroup(this.peds, px, pz, pSpeed, truckR, pvx, pvz, 2);
  }

  _contactGroup(list, px, pz, pSpeed, truckR, pvx, pvz, group) {
    const bus = this.ctx.bus;
    const track2 = IM.nearMissTrackRadius * IM.nearMissTrackRadius;

    for (let i = 0; i < list.length; i++) {
      const a = list[i];
      if (!a.active) continue;
      const dx = a.x - px, dz = a.z - pz;
      const d2 = dx * dx + dz * dz;
      if (d2 > track2) {
        if (a.nmArmed) this._fireNearMiss(a, pSpeed);
        continue;
      }
      /**
       * ⚠ THIS WAS `Math.sqrt(d2) || 1e-4` AND IT TURNED A BROKEN AGENT INTO A GUARANTEED HIT.
       *
       * NaN is FALSY. So for an agent whose position had gone non-finite: `d2` is NaN, the
       * range test `d2 > track2` above is false so it is not skipped, `Math.sqrt(NaN)` is NaN,
       * and `NaN || 1e-4` yields **1e-4** — a perfectly finite number. `gap` is then hugely
       * negative, `gap <= 0` passes, and `_hit()` fires EVERY FRAME with `nx = dx / d = NaN`,
       * straight into `ctx.player.applyImpulse`. A guard written to prevent a divide-by-zero
       * instead manufactured a collision with a broken normal.
       *
       * The zero-distance case it was actually for is real (an agent exactly on the truck's
       * axis) and is handled by the explicit `d2 > 0` test. NaN is now a separate answer:
       * skip, and let `_quarantineBroken()` recycle the agent.
       */
      if (!(d2 >= 0)) continue;             // NaN — `_quarantineBroken()` recycles it
      const d = Math.sqrt(d2);
      // Dead centre gives no meaningful normal, so the impulse is zero rather than infinite.
      const inv = d > 1e-4 ? 1 / d : 0;
      const gap = d - truckR - a.radius;

      if (gap <= 0 && a.hitCd <= 0) {
        this._hit(a, group, dx * inv, dz * inv, pSpeed, pvx, pvz);
        a.nmArmed = false; a.nmMin = 99;
        continue;
      }

      if (pSpeed > IM.nearMissMinSpeed && a.nmCd <= 0) {
        if (gap < IM.nearMissGap) {
          a.nmArmed = true;
          if (gap < a.nmMin) a.nmMin = gap;
        } else if (a.nmArmed && gap > IM.nearMissGap * 2.2) {
          this._fireNearMiss(a, pSpeed);
        }
      }
    }
  }

  _fireNearMiss(a, pSpeed) {
    a.nmArmed = false;
    const gap = a.nmMin;
    a.nmMin = 99;
    if (gap > IM.nearMissGap) return;
    a.nmCd = IM.nearMissCooldown;
    this.stats.nearMisses++;
    this.ctx.bus.emit('traffic:nearMiss', {
      position: { x: a.x, y: a.y + 0.9, z: a.z },
      gap: +Math.max(0, gap).toFixed(3),
      speed: pSpeed,
      kind: a.kind,
    });
  }

  /**
   * Arcade collision response. The car gets shoved sideways and spun; the truck gets a
   * shove back through `ctx.player.applyImpulse`, which is what makes `vehicle` emit the
   * canonical `vehicle:collided` with `other:'traffic'` (§5.1) one step later.
   */
  _hit(a, group, nx, nz, pSpeed, pvx, pvz) {
    const closing = Math.max(0, pvx * -nx + pvz * -nz);
    const strength = Math.max(closing, pSpeed * 0.5);
    a.hitCd = IM.hitCooldown;
    this.stats.hits++;

    let impulse;
    if (group === 0) {
      impulse = Math.min(IM.carImpulseMax, strength * IM.carImpulseScale);
      const side = (nx * -pvz + nz * pvx) >= 0 ? 1 : -1;
      a.latVel = side * IM.shoveLat * strength * 0.30;
      a.spin = side * Math.min(IM.shoveSpinMax, strength * 0.55);
      a.speed = Math.max(0, a.speed + IM.shoveLong * strength * 0.22);
      a.hopV = Math.min(IM.shoveHopMax, strength * 0.10);
      a.shoveT = IM.recoverTime;
      this.ctx.fx.debris(a.position, a.velocity, 'glass');
    } else if (group === 1) {
      impulse = IM.cyclistImpulse;
      a.latVel = (nx * -pvz + nz * pvx >= 0 ? 1 : -1) * 3.4;
      a.spin = 5.5;
      a.shoveT = IM.recoverTime;
      a.speed *= 0.3;
      this.ctx.fx.debris(a.position, a.velocity, 'debris');
    } else {
      impulse = IM.pedImpulse;
      a.state = PED_FLEE;
      a.fleeT = PD.fleeTime;
      a.sprawl = 0.9;
      a.speed = PD.fleeSpeed;
      this.ctx.fx.debris(a.position, a.velocity, 'debris');
    }

    // Push the truck away from the thing it hit.
    this._shove(nx * -impulse, group === 0 ? 0.35 : 0.05, nz * -impulse, group === 0 ? 'traffic' : (group === 1 ? 'cyclist' : 'ped'));

    this.ctx.bus.emit('traffic:hit', {
      kind: a.kind,
      position: { x: a.x, y: a.y + 0.6, z: a.z },
      impulse: +impulse.toFixed(3),
    });
  }

  /** Bind the truck impulse sink once, in init(), rather than branching on it every hit. */
  bindPlayerShove(fn) { this._shove = fn; }

  /** Bind the static parked field so the truck cannot drive through the kerb line. */
  bindParked(field) { this.parked = field; }

  /**
   * Truck vs. the static parked field. ONE hash-grid probe per fixed step — nine cells,
   * a handful of float compares — regardless of how many hundred cars are on the kerbs.
   * `world` does not know about these colliders, so if this were skipped the truck would
   * drive straight through the thing we added to fix the empty kerb.
   */
  _parkedContacts(dt, px, pz, pSpeed) {
    const field = this.parked;
    if (!field || field.count === 0) return;
    field.tick(dt);

    const player = this.ctx.player;
    const truckR = (player.halfWidth != null ? player.halfWidth : 1.05) + IM.truckPad;
    const i = field.overlap(px, pz, truckR);
    if (i < 0) return;

    field.markHit(i);
    field.normalAt(i, px, pz, this._n2);
    const nx = this._n2[0], nz = this._n2[1];
    const PK = T.parked;
    const impulse = Math.min(PK.impulse, Math.max(IM.cyclistImpulse, pSpeed * PK.impulseScale));

    this._shove(nx * impulse, 0.30, nz * impulse, 'traffic');
    this.stats.hits++;

    this._hitPos.set(field.px[i], player.position.y + 0.4, field.pz[i]);
    this.ctx.fx.debris(this._hitPos, player.velocity, 'glass');
    this.ctx.bus.emit('traffic:hit', {
      kind: 'sedan',
      position: { x: field.px[i], y: this._hitPos.y, z: field.pz[i] },
      impulse: +impulse.toFixed(3),
    });
  }

  // ---------------------------------------------------------------- publish

  _publish() {
    const ac = this.activeCars, ay = this.activeCyclists, ap = this.activePeds, all = this.allActive;
    ac.length = 0; ay.length = 0; ap.length = 0; all.length = 0;
    for (let i = 0; i < this.cars.length; i++) if (this.cars[i].active) { ac.push(this.cars[i]); all.push(this.cars[i]); }
    for (let i = 0; i < this.cyclists.length; i++) if (this.cyclists[i].active) { ay.push(this.cyclists[i]); all.push(this.cyclists[i]); }
    for (let i = 0; i < this.peds.length; i++) if (this.peds[i].active) { ap.push(this.peds[i]); all.push(this.peds[i]); }
  }

  nearest(x, z, maxDist) {
    let best = null, bd = maxDist != null ? maxDist * maxDist : Infinity;
    const all = this.allActive;
    for (let i = 0; i < all.length; i++) {
      const a = all[i];
      const dx = a.x - x, dz = a.z - z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bd) { bd = d2; best = a; }
    }
    return best;
  }
}

export { PED_WALK, PED_CROSS, PED_FLEE };
